import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAdminQuestionnaire } from '../../api';
import useNoIndex from '../../hooks/useNoIndex';

const CLAUDE_PROMPT = `I want you to write a questionnaire as a single JSON object, in the exact format below. Ask me what the survey should be about, how many dimensions (categories) and questions I want, and anything else you need — then output ONLY the JSON, nothing else.

FORMAT:
{
  "slug": "lowercase-hyphenated-id",          // used in the URL, must be unique, letters/numbers/hyphens only
  "title": "Human-readable title",
  "description": "One sentence, internal use only",
  "dimensions": [
    { "key": "shortCamelCaseId", "label": "Human-readable name" }
    // one entry per category. "key" is an internal id — short, unique, no spaces.
  ],
  "questions": [
    {
      "id": "q1",                             // unique id, "q1", "q2", etc. is fine
      "dimensionKey": "shortCamelCaseId",      // must match one of the dimensions' "key" above
      "text": "The question, phrased to the reader",
      "options": [
        { "value": 0, "label": "Weakest answer" },
        { "value": 4, "label": "Strongest answer" }
        // 2+ options, each with a unique non-negative "value" (points) and a "label" (answer text).
        // Values don't have to be 0-4 or 5 options — any scale works, but stay consistent within one question.
      ]
    }
    // every dimension needs at least one question, and every question needs a dimensionKey that exists above.
  ],
  "tiers": [
    { "min": 0, "max": 8, "name": "Tier name", "description": "Shown on the results page for this score range." }
    // IMPORTANT: tiers must start at 0, have no gaps or overlaps, and the last tier's "max" must equal
    // the questionnaire's max possible score (sum, across all questions, of that question's highest option value).
    // Compute that total AFTER you've written the questions, then build tiers to exactly cover 0 through that total.
  ],
  "quickWins": {
    "q1": "A short, concrete action tied to scoring low on this specific question."
    // optional — one entry per question id, only for questions where you have a specific suggestion.
  },
  "benchmarkPercent": 40,                     // optional, 0-100, a reference line shown on the results radar chart
  "contextFields": {                          // optional — dropdowns collected on the last step, not scored
    "employees": ["1–10", "11–25", "26+"],
    "revenue": ["Under $1M", "$1–5M", "$5M+"]
  },
  "splash": {                                 // optional — shown before the first question at /scorecard/<slug>
    "heroImage": "",                          // optional URL; if omitted, splash renders as plain text (no banner)
    "headline": "Headline for the splash page",   // optional, falls back to "title" above
    "description": "One-sentence pitch for the diagnostic.",  // optional lede paragraph
    "bullets": [
      "Takes less than 10 minutes. One question at a time."
      // optional. Each bullet's first sentence (up to the first . ! or ?) is bolded automatically —
      // write a short bold lead-in as its own sentence, then the rest as plain text.
    ],
    "ctaText": "See your score"               // optional, defaults to "See your score"
  }
}

WORKED EXAMPLE (2 dimensions, 4 questions, max score 16):
{
  "slug": "retail-readiness",
  "title": "Retail Ops Readiness",
  "description": "Quick check on inventory and staffing basics.",
  "dimensions": [
    { "key": "inventory", "label": "Inventory Management" },
    { "key": "staffing", "label": "Staffing" }
  ],
  "questions": [
    { "id": "q1", "dimensionKey": "inventory", "text": "How do you track stock levels?", "options": [
      { "value": 0, "label": "We don't, really." },
      { "value": 2, "label": "Spreadsheet, updated sometimes." },
      { "value": 4, "label": "Real-time POS-integrated system." }
    ]},
    { "id": "q2", "dimensionKey": "inventory", "text": "How often do you run out of a top seller?", "options": [
      { "value": 0, "label": "Often." },
      { "value": 4, "label": "Rarely." }
    ]},
    { "id": "q3", "dimensionKey": "staffing", "text": "Do you have a documented onboarding process?", "options": [
      { "value": 0, "label": "No." },
      { "value": 4, "label": "Yes, and it's used consistently." }
    ]},
    { "id": "q4", "dimensionKey": "staffing", "text": "How do you handle shift scheduling?", "options": [
      { "value": 0, "label": "Ad hoc texts/calls." },
      { "value": 4, "label": "Dedicated scheduling software." }
    ]}
  ],
  "tiers": [
    { "min": 0, "max": 8, "name": "Getting Started", "description": "The basics aren't in place yet." },
    { "min": 9, "max": 16, "name": "Dialed In", "description": "Solid systems, minor gaps at most." }
  ],
  "quickWins": {
    "q1": "Pick your top 10 sellers and start tracking just those in a shared spreadsheet this week."
  },
  "benchmarkPercent": 35,
  "splash": {
    "headline": "How ready is your retail operation?",
    "description": "A quick diagnostic on inventory and staffing basics.",
    "bullets": [
      "Takes less than 5 minutes. Two dimensions, four questions.",
      "A score and a plan. See your tier and where to focus first."
    ],
    "ctaText": "See your score"
  }
}

Now ask me about the survey I want, then output the finished JSON.`;

export default function QuestionnaireImport() {
  useNoIndex();
  const navigate = useNavigate();
  const [json, setJson] = useState('');
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  function copyPrompt() {
    navigator.clipboard.writeText(CLAUDE_PROMPT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleImport(e) {
    e.preventDefault();
    setErrors([]);

    let payload;
    try {
      payload = JSON.parse(json);
    } catch (err) {
      setErrors([`That's not valid JSON: ${err.message}`]);
      return;
    }

    setSaving(true);
    try {
      const record = await createAdminQuestionnaire(payload);
      navigate(`/admin/questionnaires/${record.id}`);
    } catch (err) {
      setErrors(err?.response?.data?.errors || [err?.response?.data?.error || 'Import failed.']);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page page--wide">
      <div className="admin-toolbar">
        <Link to="/admin/questionnaires">← All questionnaires</Link>
        <span />
      </div>

      <h1>Import a questionnaire</h1>
      <p className="muted small">
        Have Claude write the survey for you, then paste the result below. It'll land in the same
        editor afterward so you can tweak anything by hand.
      </p>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3>Step 1 — get the JSON from Claude</h3>
        <p className="muted small">
          Start a new conversation with Claude and paste this prompt in. It will ask you what the
          survey should cover, then hand back the finished file.
        </p>
        <button type="button" className="secondary" onClick={copyPrompt}>
          {copied ? 'Copied!' : 'Copy prompt for Claude'}
        </button>
        <details style={{ marginTop: '1rem' }}>
          <summary className="muted small" style={{ cursor: 'pointer' }}>Show the prompt text</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', marginTop: '0.75rem' }}>{CLAUDE_PROMPT}</pre>
        </details>
      </div>

      <form onSubmit={handleImport}>
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Step 2 — paste the JSON here</h3>
          {errors.length > 0 && (
            <div className="error">
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          <textarea
            rows={16}
            value={json}
            onChange={(e) => setJson(e.target.value)}
            placeholder="Paste the JSON Claude gave you here…"
            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            required
          />
        </div>

        <div className="nav-row">
          <span className="muted small">This creates and publishes the questionnaire immediately.</span>
          <button type="submit" disabled={saving || !json.trim()}>
            {saving ? 'Importing…' : 'Import questionnaire'}
          </button>
        </div>
      </form>
    </div>
  );
}
